// components/tabs/STD215Tab.jsx
// Interactive STD 215 form — looks like the actual DGS form, saves to s215

import { useState, useRef } from 'react';

// ── Form primitives that mimic the paper form ──────────────────────────────

function FormBox({ children, style }) {
  return (
    <div style={{
      border: '1px solid #333', position: 'relative',
      ...style,
    }}>
      {children}
    </div>
  );
}

function FieldLabel({ children, style }) {
  return (
    <div style={{
      fontSize: 8, fontWeight: 700, color: '#222',
      lineHeight: 1.2, padding: '3px 3px 1px 3px',
      ...style,
    }}>
      {children}
    </div>
  );
}

function FormInput({ value, onChange, placeholder, style, monospace, type = 'text' }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        border: 'none', outline: 'none', background: 'transparent',
        fontSize: 11, fontFamily: monospace ? 'var(--font-mono)' : 'inherit',
        padding: '2px 3px 4px 3px', width: '100%', boxSizing: 'border-box',
        color: '#111', minHeight: 20,
        ...style,
      }}
    />
  );
}

function FormTextarea({ value, onChange, rows = 3, placeholder }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        border: 'none', outline: 'none', background: 'transparent',
        fontSize: 10.5, fontFamily: 'inherit', resize: 'none',
        padding: '2px 3px', width: '100%', boxSizing: 'border-box',
        color: '#111', lineHeight: 1.5,
      }}
    />
  );
}

function FormCheckbox({ checked, onChange, label, style }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 9.5, ...style }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 10, height: 10, border: '1px solid #333', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', background: 'white',
        }}
      >
        {checked && <span style={{ fontSize: 9, lineHeight: 1, color: '#111' }}>✕</span>}
      </div>
      {label && <span>{label}</span>}
    </label>
  );
}

function FormSelect({ value, onChange, options, style }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{
        border: 'none', outline: 'none', background: 'transparent',
        fontSize: 10.5, padding: '1px 2px', cursor: 'pointer',
        color: '#111', width: '100%',
        ...style,
      }}
    >
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  );
}

// Row/col layout helpers
function Row({ children, style }) {
  return <div style={{ display: 'flex', ...style }}>{children}</div>;
}

function Col({ children, flex, style }) {
  return (
    <div style={{
      flex: flex || 1, borderRight: '1px solid #333',
      minHeight: 28, overflow: 'visible',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Page components ────────────────────────────────────────────────────────

function PageHeader({ agreementNum, amendmentNum, scoId, pageNum }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 700, marginBottom: 2 }}>
        SCO ID: {scoId || ''}
      </div>
      <Row style={{ alignItems: 'flex-end', marginBottom: 4 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 8.5 }}>STATE OF CALIFORNIA</div>
          <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: '-.3px' }}>AGREEMENT SUMMARY</div>
          <div style={{ fontSize: 7.5 }}>STD 215 (Rev. 04/2020)</div>
        </div>
        <div style={{ display: 'flex', border: '1px solid #333' }}>
          <div style={{ padding: '3px 8px', borderRight: '1px solid #333', minWidth: 140 }}>
            <div style={{ fontSize: 7, fontWeight: 700 }}>AGREEMENT NUMBER</div>
            <div style={{ fontSize: 11, fontWeight: 700, marginTop: 2 }}>{agreementNum || ''}</div>
          </div>
          <div style={{ padding: '3px 8px', minWidth: 120 }}>
            <div style={{ fontSize: 7, fontWeight: 700 }}>AMENDMENT NUMBER</div>
            <div style={{ fontSize: 11, marginTop: 2 }}>{amendmentNum || ''}</div>
          </div>
        </div>
      </Row>
    </div>
  );
}

function Page1({ s215, contract, profile, updateS215 }) {
  const pt = s215.paymentTerms || ['Itemized Invoice'];
  const togglePt = (term) => {
    const updated = pt.includes(term) ? pt.filter(t => t !== term) : [...pt, term];
    updateS215('paymentTerms', updated);
  };
  const withholdChecked = pt.includes('Withhold');
  const advNteChecked = pt.includes('Adv/NTE');

  const scoId = s215.scoId || ((profile.scoAgencyCode || '3355') + (contract.contractNum ? '-' + contract.contractNum : ''));

  return (
    <div style={{ fontSize: 9.5, fontFamily: 'Arial, sans-serif' }}>
      <PageHeader
        agreementNum={s215.agreementNum || contract.contractNum}
        amendmentNum={s215.amendmentNum}
        scoId={scoId}
        pageNum={1}
      />

      {/* Additional pages checkbox */}
      <FormBox style={{ marginBottom: -1, padding: '3px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <FormCheckbox checked={!!s215.additionalPages} onChange={v => updateS215('additionalPages', v)} />
        <span style={{ fontSize: 8.5, fontWeight: 700 }}>CHECK HERE IF ADDITIONAL PAGES ARE ATTACHED</span>
      </FormBox>

      {/* Fields 1 & 2 */}
      <Row>
        <Col flex={3} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>1. CONTRACTOR'S NAME</FieldLabel>
          <FormInput value={s215.contractorName} onChange={v => updateS215('contractorName', v)} placeholder="Contractor name" style={{ fontSize: 12, padding: '3px' }} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>2. FEDERAL I.D. NUMBER</FieldLabel>
          <FormInput value={s215.contractorTaxId} onChange={v => updateS215('contractorTaxId', v)} placeholder="XX-XXXXXXX" monospace style={{ fontSize: 11 }} />
        </Col>
      </Row>

      {/* DBA — not part of the official STD 215 form, but used throughout
          GoldenScope for correct contractor display: "Legal Name dba DBA Name".
          Populated from the vendor library when a vendor is selected, or
          entered manually here. */}
      <Row>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>DBA (if applicable)</FieldLabel>
          <FormInput value={s215.contractorDba || ''} onChange={v => updateS215('contractorDba', v)} placeholder="Doing Business As name (leave blank if none)" style={{ fontSize: 12, padding: '3px' }} />
          {s215.contractorName && s215.contractorDba && (
            <div style={{ fontSize: 10, color: '#16a34a', marginTop: 2, padding: '0 3px' }}>
              Will display as: <strong>{s215.contractorName} dba {s215.contractorDba}</strong>
            </div>
          )}
        </Col>
      </Row>

      {/* Fields 3, 4, 5 */}
      <Row>
        <Col flex={2} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>3. AGENCY TRANSMITTING AGREEMENT</FieldLabel>
          <FormInput value={s215.agencyOverride !== undefined ? s215.agencyOverride : (profile.agencyName || '')} onChange={v => updateS215('agencyOverride', v)} placeholder={profile.agencyName || 'Agency'} />
        </Col>
        <Col flex={2} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>4. DIVISION, BUREAU, OR OTHER UNIT</FieldLabel>
          <FormInput value={s215.divisionOverride !== undefined ? s215.divisionOverride : (profile.agencyDivision || '')} onChange={v => updateS215('divisionOverride', v)} placeholder={profile.agencyDivision || 'Division'} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>5. AGENCY BILLING CODE</FieldLabel>
          <FormInput value={s215.agencyBillingCode || profile.agencyBillingCode} onChange={v => updateS215('agencyBillingCode', v)} monospace />
        </Col>
      </Row>

      {/* Fields 6a, 6b, 6c */}
      <Row>
        <Col flex={2} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>6a. CONTRACT ANALYST NAME</FieldLabel>
          <FormInput value={s215.contactName || profile.contactName} onChange={v => updateS215('contactName', v)} />
        </Col>
        <Col flex={2} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>6b. EMAIL</FieldLabel>
          <FormInput value={s215.contactEmail || profile.contactEmail} onChange={v => updateS215('contactEmail', v)} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>6c. PHONE NUMBER</FieldLabel>
          <FormInput value={s215.contactPhone || profile.contactPhone} onChange={v => updateS215('contactPhone', v)} />
        </Col>
      </Row>

      {/* Field 7 */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '3px 6px' }}>
        <div style={{ fontSize: 8, fontWeight: 700, marginBottom: 3 }}>7. HAS YOUR AGENCY CONTRACTED FOR THESE SERVICES BEFORE?</div>
        <Row style={{ gap: 16, alignItems: 'center' }}>
          <FormCheckbox checked={!s215.priorContract} onChange={() => updateS215('priorContract', false)} label="No" />
          <FormCheckbox checked={!!s215.priorContract} onChange={() => updateS215('priorContract', true)} label="Yes (If Yes, enter prior Contractor Name and Agreement Number)" />
        </Row>
        {s215.priorContract && (
          <Row style={{ gap: 8, marginTop: 4 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 7, color: '#666' }}>PRIOR CONTRACTOR NAME</div>
              <FormInput value={s215.priorContractorName} onChange={v => updateS215('priorContractorName', v)} style={{ borderBottom: '1px solid #999' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 7, color: '#666' }}>PRIOR AGREEMENT NUMBER</div>
              <FormInput value={s215.priorAgreementNum} onChange={v => updateS215('priorAgreementNum', v)} monospace style={{ borderBottom: '1px solid #999' }} />
            </div>
          </Row>
        )}
      </FormBox>

      {/* Field 8 */}
      <FormBox style={{ borderTop: '1px solid #333', minHeight: 48 }}>
        <FieldLabel>8. BRIEF DESCRIPTION OF SERVICES</FieldLabel>
        <FormTextarea value={s215.descriptionOverride !== undefined ? s215.descriptionOverride : (contract.title || '')} onChange={v => updateS215('descriptionOverride', v)} rows={2} placeholder="Brief description" />
      </FormBox>

      {/* Field 9 */}
      <FormBox style={{ borderTop: '1px solid #333', minHeight: 64 }}>
        <FieldLabel>9. AGREEMENT OUTLINE <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Include reason for Agreement: Identify specific problem, administrative requirement, program need or other circumstances making the Agreement necessary; include special or unusual terms and conditions.)</span></FieldLabel>
        <FormTextarea value={s215.agreeOutline || contract.description} onChange={v => updateS215('agreeOutline', v)} rows={4} placeholder="Describe the agreement purpose and basis for contracting..." />
      </FormBox>

      {/* Field 10 — Payment Terms */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '3px 6px' }}>
        <div style={{ fontSize: 8, fontWeight: 700, marginBottom: 4 }}>10. PAYMENT TERMS <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(More than one may apply)</span></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', alignItems: 'center' }}>
          {['Monthly Flat Rate', 'Quarterly', 'One-Time Payment', 'Progress Payment', 'Itemized Invoice'].map(term => (
            <FormCheckbox key={term} checked={pt.includes(term)} onChange={() => togglePt(term)} label={term} />
          ))}

          {/* Withhold — shows % field when checked */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <FormCheckbox checked={withholdChecked} onChange={() => togglePt('Withhold')} label="Withhold" />
            {withholdChecked && (
              <>
                <input
                  value={s215.withholdPct || ''}
                  onChange={e => updateS215('withholdPct', e.target.value)}
                  placeholder="%"
                  style={{ border: 'none', borderBottom: '1px solid #aaa', outline: 'none', fontSize: 9, width: 36, background: 'transparent', textAlign: 'center' }}
                />
                <span style={{ fontSize: 9 }}>%</span>
              </>
            )}
          </div>

          {/* Advanced Payment / Not To Exceed — merged checkbox, shows amount field when checked */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <FormCheckbox checked={advNteChecked} onChange={() => togglePt('Adv/NTE')} label="Advanced Payment / Not To Exceed" />
            {advNteChecked && (
              <>
                <span style={{ fontSize: 9 }}>$</span>
                <input
                  value={s215.advNteAmount || ''}
                  onChange={e => updateS215('advNteAmount', e.target.value)}
                  placeholder="Amount or %"
                  style={{ border: 'none', borderBottom: '1px solid #aaa', outline: 'none', fontSize: 9, width: 80, background: 'transparent' }}
                />
              </>
            )}
          </div>

          <FormCheckbox checked={pt.includes('Reimbursement')} onChange={() => togglePt('Reimbursement')} label="Reimbursement" />
          <FormCheckbox checked={pt.includes('Other')} onChange={() => togglePt('Other')} label="Other" />
        </div>
      </FormBox>

      {/* Field 11 — Projected Expenditures */}
      <div style={{ fontSize: 8, fontWeight: 700, marginTop: 4, marginBottom: 1 }}>11. PROJECTED EXPENDITURES</div>
      <FormBox>
        {/* Header */}
        <Row style={{ borderBottom: '1px solid #333', background: '#f5f5f5' }}>
          {['FUND TITLE', 'ITEM', 'FISCAL YEAR', 'CHAPTER', 'STATUTE', 'PROJECTED EXPENDITURES'].map((h, i) => (
            <div key={h} style={{ flex: i === 0 ? 2 : 1, padding: '2px 3px', fontSize: 7, fontWeight: 700, borderRight: i < 5 ? '1px solid #333' : 'none' }}>
              {h}
            </div>
          ))}
        </Row>
        {/* 4 data rows */}
        {(s215.fundingLines?.length > 0 ? s215.fundingLines : [{},{},{},{}]).slice(0, 4).map((row, idx) => (
          <Row key={idx} style={{ borderBottom: '1px solid #ddd', minHeight: 18 }}>
            {['fundTitle','item','fiscalYear','chapter','statute','amount'].map((key, i) => (
              <div key={key} style={{ flex: i === 0 ? 2 : 1, borderRight: i < 5 ? '1px solid #ddd' : 'none' }}>
                <FormInput
                  value={row[key] || ''}
                  onChange={v => {
                    const lines = s215.fundingLines?.length > 0 ? [...s215.fundingLines] : [{},{},{},{}];
                    while (lines.length < 4) lines.push({});
                    lines[idx] = { ...lines[idx], [key]: v };
                    updateS215('fundingLines', lines);
                  }}
                  monospace={key === 'amount'}
                  style={{ fontSize: 10 }}
                />
              </div>
            ))}
          </Row>
        ))}
        {/* Object Code + Agreement Total */}
        <Row style={{ borderTop: '1px solid #333' }}>
          <div style={{ flex: 3, padding: '2px 3px', borderRight: '1px solid #333', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 7, fontWeight: 700, whiteSpace: 'nowrap' }}>OBJECT CODE</span>
            <FormInput value={s215.objectCode} onChange={v => updateS215('objectCode', v)} monospace style={{ flex: 1 }} />
          </div>
          <div style={{ flex: 3, padding: '2px 3px', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 7, fontWeight: 700 }}>AGREEMENT TOTAL</span>
            <FormInput value={s215.maxAmount || contract.budget || ''} onChange={v => updateS215('maxAmount', v)} monospace placeholder="" style={{ flex: 1 }} />
          </div>
        </Row>
        {/* Optional Use — editable top area + encumbered amounts */}
        <Row style={{ borderTop: '1px solid #333', minHeight: 80 }}>
          <div style={{ flex: 3, borderRight: '1px solid #333', padding: '3px 4px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 8, fontStyle: 'italic', marginBottom: 4 }}>OPTIONAL USE</div>
            <FormTextarea
              value={s215.optionalUse || ''}
              onChange={v => updateS215('optionalUse', v)}
              rows={4}
              placeholder="Optional use notes..."
            />
          </div>
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, borderBottom: '1px solid #ddd', padding: '2px 4px' }}>
              <div style={{ fontSize: 7 }}>AMOUNT ENCUMBERED BY THIS DOCUMENT</div>
              <FormInput value={s215.amountEncumbered || ''} onChange={v => updateS215('amountEncumbered', v)} monospace style={{ fontSize: 10 }} />
            </div>
            <div style={{ flex: 1, borderBottom: '1px solid #ddd', padding: '2px 4px' }}>
              <div style={{ fontSize: 7 }}>PRIOR AMOUNT ENCUMBERED FOR THIS AGREEMENT</div>
              <FormInput value={s215.priorAmountEncumbered || ''} onChange={v => updateS215('priorAmountEncumbered', v)} monospace style={{ fontSize: 10 }} />
            </div>
            <div style={{ flex: 1, padding: '2px 4px' }}>
              <div style={{ fontSize: 7 }}>TOTAL AMOUNT ENCUMBERED TO DATE</div>
              <FormInput value={s215.totalAmountEncumbered || ''} onChange={v => updateS215('totalAmountEncumbered', v)} monospace style={{ fontSize: 10 }} />
            </div>
          </div>
        </Row>
        {/* Certification clause — read-only, own bordered row at bottom */}
        <Row style={{ borderTop: '1px solid #333' }}>
          <div style={{ flex: 1, border: '1px solid #333', borderTop: 'none', borderLeft: 'none', padding: '4px 6px' }}>
            <div style={{ fontSize: 7, fontStyle: 'italic', color: '#444', lineHeight: 1.6 }}>
              I certify upon my own personal knowledge that the budgeted funds for the current budget year are available for the period and purpose of the expenditure stated above.
            </div>
          </div>
        </Row>
        {/* Accounting Officer */}
        <Row style={{ borderTop: '1px solid #333', minHeight: 22 }}>
          <Col flex={2} style={{ borderLeft: '1px solid #333' }}>
            <FieldLabel>ACCOUNTING OFFICER'S SIGNATURE</FieldLabel>
          </Col>
          <Col flex={2} style={{}}>
            <FieldLabel>ACCOUNTING OFFICER'S NAME <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Print or Type)</span></FieldLabel>
            <FormInput value={s215.accountingOfficerOverride !== undefined ? s215.accountingOfficerOverride : (profile.accountingOfficerName || '')} onChange={v => updateS215('accountingOfficerOverride', v)} placeholder={profile.accountingOfficerName || 'Accounting Officer'} />
          </Col>
          <Col style={{ borderRight: '1px solid #333' }}>
            <FieldLabel>DATE SIGNED</FieldLabel>
          </Col>
        </Row>
      </FormBox>
    </div>
  );
}

function Page2({ s215, contract, profile, paal, updateS215 }) {
  const scoId = s215.scoId || ((profile.scoAgencyCode || '3355') + (contract.contractNum ? '-' + contract.contractNum : ''));
  // Use individual overrides if set, otherwise fall back to contract procMethod
  const effectiveMethod = s215.procMethodOverride || contract.procMethod || '';
  const isRFP = s215.bidRFP !== undefined ? !!s215.bidRFP : ['RFP','RFO','RFQ'].includes(effectiveMethod);
  const isIFB = s215.bidIFB !== undefined ? !!s215.bidIFB : effectiveMethod === 'IFB';
  const isSSC = s215.soleSource !== undefined ? !!s215.soleSource : ['NCB','Sole Source'].includes(effectiveMethod);
  const isExempt = s215.biddingExempt !== undefined ? !!s215.biddingExempt : effectiveMethod === 'Exempt';
  const isOther = s215.bidOther !== undefined ? !!s215.bidOther : (!isRFP && !isIFB && !isSSC && !isExempt && !!effectiveMethod);

  const amendments = contract.amendments || [];
  const amendmentRows = s215.amendmentRows || [{},{},{}];

  const updateAmendmentRow = (idx, key, val) => {
    const rows = [...(s215.amendmentRows || [{},{},{}])];
    while (rows.length < 3) rows.push({});
    rows[idx] = { ...rows[idx], [key]: val };
    updateS215('amendmentRows', rows);
  };

  return (
    <div style={{ fontSize: 9.5, fontFamily: 'Arial, sans-serif' }}>
      <PageHeader
        agreementNum={s215.agreementNum || contract.contractNum}
        amendmentNum={s215.amendmentNum}
        scoId={scoId}
        pageNum={2}
      />

      {/* Field 12 — Agreement table */}
      <div style={{ fontSize: 8, fontWeight: 700, marginBottom: 1 }}>12. AGREEMENT</div>
      <FormBox>
        <Row style={{ borderBottom: '1px solid #333', background: '#f5f5f5' }}>
          {['AGREEMENT','TERM FROM','TERM THROUGH','TOTAL COST OF THIS TRANSACTION','BID, SOLE SOURCE, EXEMPT'].map((h,i) => (
            <div key={h} style={{ flex: i===4?2:1, padding: '3px 4px', fontSize: 7, fontWeight: 700, borderRight: i<4?'1px solid #333':'none' }}>{h}</div>
          ))}
        </Row>
        {/* Original row */}
        <Row style={{ borderBottom: '1px solid #ddd', minHeight: 20 }}>
          <div style={{ flex: 1, padding: '2px 4px', fontSize: 10, borderRight: '1px solid #ddd' }}>Original</div>
          <div style={{ flex: 1, borderRight: '1px solid #ddd' }}>
            <FormInput value={s215.startDate||''} onChange={v => updateS215('startDate', v)} type="date" style={{ fontSize: 10 }} />
          </div>
          <div style={{ flex: 1, borderRight: '1px solid #ddd' }}>
            <FormInput value={s215.endDate||''} onChange={v => updateS215('endDate', v)} type="date" style={{ fontSize: 10 }} />
          </div>
          <div style={{ flex: 1, borderRight: '1px solid #ddd' }}>
            <FormInput value={s215.maxAmount && s215.maxAmount !== 'Not specified' ? s215.maxAmount : ''} onChange={v => updateS215('maxAmount', v)} monospace style={{ fontSize: 10 }} />
          </div>
          <div style={{ flex: 2 }}>
            <FormInput value={s215.bidSourceOriginal||''} onChange={v => updateS215('bidSourceOriginal', v)} style={{ fontSize: 10 }} />
          </div>
        </Row>
        {/* Amendment rows 1–3 */}
        {[0,1,2].map(idx => (
          <Row key={idx} style={{ borderBottom: '1px solid #ddd', minHeight: 20 }}>
            <div style={{ flex: 1, padding: '2px 4px', fontSize: 10, borderRight: '1px solid #ddd' }}>Amendment {idx+1}</div>
            <div style={{ flex: 1, borderRight: '1px solid #ddd' }}>
              <FormInput value={amendmentRows[idx]?.startDate||''} onChange={v => updateAmendmentRow(idx,'startDate',v)} type="date" style={{ fontSize: 10 }} />
            </div>
            <div style={{ flex: 1, borderRight: '1px solid #ddd' }}>
              <FormInput value={amendmentRows[idx]?.endDate||''} onChange={v => updateAmendmentRow(idx,'endDate',v)} type="date" style={{ fontSize: 10 }} />
            </div>
            <div style={{ flex: 1, borderRight: '1px solid #ddd' }}>
              <FormInput value={amendmentRows[idx]?.cost||''} onChange={v => updateAmendmentRow(idx,'cost',v)} monospace style={{ fontSize: 10 }} />
            </div>
            <div style={{ flex: 2 }}>
              <FormInput value={amendmentRows[idx]?.bidSource||''} onChange={v => updateAmendmentRow(idx,'bidSource',v)} style={{ fontSize: 10 }} />
            </div>
          </Row>
        ))}
        <Row style={{ borderTop: '1px solid #333', minHeight: 18 }}>
          <div style={{ flex: 3, textAlign: 'right', padding: '3px 8px', fontSize: 9, fontWeight: 700 }}>TOTAL</div>
          <div style={{ flex: 2, borderLeft: '1px solid #333' }} />
        </Row>
      </FormBox>

      {/* Field 13 — Bidding Method */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '4px 6px 0 6px' }}>
        <div style={{ fontSize: 8, fontWeight: 700, marginBottom: 4 }}>13. BIDDING METHOD USED</div>

        {/* Two-column checkbox area — divider only covers this section */}
        <Row style={{ gap: 0, alignItems: 'stretch', marginBottom: 4 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, borderRight: '1px solid #ccc', paddingRight: 8, paddingBottom: 4 }}>
            <FormCheckbox checked={isRFP} onChange={v => updateS215('bidRFP', v)} label="Request for Proposal (RFP) (Attach justification if secondary method is used)" />
            <FormCheckbox checked={isIFB} onChange={v => updateS215('bidIFB', v)} label="Invitation for Bid (IFB)" />
            <FormCheckbox checked={isOther} onChange={v => updateS215('bidOther', v)} label="Other (explain below)" />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 8, paddingBottom: 4 }}>
            <FormCheckbox checked={s215.useMSA||false} onChange={v => updateS215('useMSA', v)} label="Use of Master Service Agreement" />
            <FormCheckbox checked={isExempt||s215.biddingExempt||false} onChange={v => updateS215('biddingExempt', v)} label="Exempt from Bidding (Give authority for exempt status)" />
            <FormCheckbox checked={isSSC||s215.soleSource||false} onChange={v => updateS215('soleSource', v)} label="Sole Source Contract (Attach STD. 821)" />
          </div>
        </Row>

        {/* Full-width Other text field — always visible, spans entire box width */}
        <div style={{ borderTop: '1px solid #ccc', paddingTop: 4, paddingBottom: 4 }}>
          <div style={{ fontSize: 7, fontWeight: 700, marginBottom: 2, color: '#444' }}>OTHER — EXPLAIN:</div>
          <FormInput
            value={s215.otherMethodExplain !== undefined ? s215.otherMethodExplain : (isOther ? (contract.procMethod || '') : '')}
            onChange={v => updateS215('otherMethodExplain', v)}
            placeholder="Describe other bidding method used..."
            style={{ width: '100%', fontSize: 10 }}
          />
        </div>

        <div style={{ fontSize: 7.5, fontStyle: 'italic', borderTop: '1px solid #ccc', paddingTop: 3, paddingBottom: 4, color: '#444' }}>
          <em>Note: Proof of advertisement in the State Contracts Register or an approved form STD. 821, Contract Advertising Exemption Request, must be attached</em>
        </div>
      </FormBox>

      {/* Fields 14-16 */}
      {[
        { num: '14', label: 'SUMMARY OF BIDS', italic: '(List of bidders, bid amount and small business status) (If an amendment, sole source, or exempt, leave blank)', key: 'summaryBids', rows: 3 },
        { num: '15', label: 'IF AWARD OF AGREEMENT IS TO OTHER THAN THE LOWER BIDDER, EXPLAIN REASON(S)', italic: '(If an amendment, sole source, or exempt, leave blank)', key: 'lowerBidderReason', rows: 2 },
        { num: '16', label: 'WHAT IS THE BASIS FOR DETERMINING THAT THE PRICE OR RATE IS REASONABLE?', italic: '', key: 'priceBasis', rows: 2 },
      ].map(({ num, label, italic, key, rows }) => (
        <FormBox key={num} style={{ borderTop: '1px solid #333', padding: '3px 6px' }}>
          <div style={{ fontSize: 8, fontWeight: 700 }}>{num}. {label} <span style={{ fontWeight: 400, fontStyle: 'italic' }}>{italic}</span></div>
          <FormTextarea value={s215[key]} onChange={v => updateS215(key, v)} rows={rows} placeholder={`Enter ${label.toLowerCase()}...`} />
        </FormBox>
      ))}

      {/* Field 17a */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '4px 6px' }}>
        <div style={{ fontSize: 8, fontWeight: 700, marginBottom: 4 }}>17a. JUSTIFICATION FOR CONTRACTING OUT <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Check one)</span></div>
        <Row style={{ gap: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, borderRight: '1px solid #ccc', paddingRight: 8 }}>
            <FormCheckbox checked={(s215.gc19130||'b')==='a'} onChange={() => updateS215('gc19130','a')} label="Contracting out is based on cost savings per Government Code 19130(a). The State Personnel Board has been so notified." />
            <FormCheckbox checked={(s215.gc19130||'b')==='na'} onChange={() => updateS215('gc19130','na')} label="Not Applicable (Interagency / Public Works / Other)" />
          </div>
          <div style={{ flex: 1, paddingLeft: 8 }}>
            <FormCheckbox checked={(s215.gc19130||'b')==='b'} onChange={() => updateS215('gc19130','b')} label="Contracting out is justified based on Government Code 19130(b). When this box is checked, a completed JUSTIFICATION - CALIFORNIA CODE OF REGULATIONS, TITLE 2, SECTION 547.60 must be attached." />
          </div>
        </Row>
      </FormBox>

      {/* Field 17b */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '4px 6px' }}>
        <div style={{ fontSize: 8, fontWeight: 700, marginBottom: 3 }}>17b. EMPLOYEE BARGAINING UNIT NOTIFICATION</div>
        <FormCheckbox
          checked={s215.bargainingUnitNotified !== false}
          onChange={v => updateS215('bargainingUnitNotified', v)}
          label="By checking this box, I hereby certify compliance with Government Code section 19132(b)(1)."
        />
      </FormBox>

      {/* Authorized Signature */}
      <Row style={{ borderBottom: '1px solid #333' }}>
        <Col flex={2} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', minHeight: 28 }}>
          <FieldLabel>AUTHORIZED SIGNATURE</FieldLabel>
        </Col>
        <Col flex={3} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>SIGNER'S NAME <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Print or Type)</span></FieldLabel>
          <FormInput value={(s215.signatoryName || profile.signatoryName || '') + (s215.signatoryTitle || profile.signatoryTitle ? ', ' + (s215.signatoryTitle || profile.signatoryTitle) : '')} onChange={v => updateS215('signatoryNameOverride', v)} style={{ fontSize: 11 }} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>DATE SIGNED</FieldLabel>
        </Col>
      </Row>

      {/* Fields 18-23 grid */}
      <div style={{ display: 'flex', border: '1px solid #333', borderTop: 'none' }}>
        {/* Left 62% */}
        <div style={{ flex: 62, borderRight: '1px solid #333' }}>
          {[
            { num: '18', label: 'FOR AGREEMENTS IN EXCESS OF $5,000: Has the letting of the agreement been reported to the Department of Fair Employment and Housing?', key: 'fairEmployment', opts: ['No','Yes','N/A'] },
            { num: '19', label: 'HAVE CONFLICT OF INTEREST ISSUES BEEN IDENTIFIED AND RESOLVED AS REQUIRED BY THE STATE CONTRACT MANUAL SECTION 7.10?', key: 'conflictOfInterest', opts: ['No','Yes','N/A'] },
            { num: '20', label: 'FOR CONSULTING AGREEMENTS: Did you review any contractor evaluations on file with the DGS Legal Office?', key: 'consultingEvals', opts: ['None on file','No','Yes','N/A'] },
          ].map(({ num, label, key, opts }) => (
            <div key={num} style={{ padding: '3px 5px', borderBottom: '1px solid #ccc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 7.5, flex: 1, marginRight: 8 }}><strong>{num}.</strong> {label}</div>
              <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                {opts.map(opt => (
                  <FormCheckbox key={opt} checked={(s215[key]||opts[opts.length-1])===opt} onChange={() => updateS215(key, opt)} label={opt} />
                ))}
              </div>
            </div>
          ))}
          {/* Field 21 */}
          <div style={{ padding: '3px 5px' }}>
            <div style={{ fontSize: 7.5, fontWeight: 700, marginBottom: 3 }}>21. IS A SIGNED COPY OF THE FOLLOWING ON FILE AT YOUR AGENCY FOR THIS CONTRACTOR?</div>
            <Row style={{ gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 7.5, marginBottom: 3 }}>A. Contractor Certification Clauses</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['No','Yes','N/A'].map(opt => <FormCheckbox key={opt} checked={(s215.certClauses||'Yes')===opt} onChange={() => updateS215('certClauses', opt)} label={opt} />)}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 7.5, marginBottom: 3 }}>B. STD 204 Vendor Data Record</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['No','Yes','N/A'].map(opt => <FormCheckbox key={opt} checked={(s215.std204||'Yes')===opt} onChange={() => updateS215('std204', opt)} label={opt} />)}
                </div>
              </div>
            </Row>
          </div>
        </div>
        {/* Right 38% */}
        <div style={{ flex: 38 }}>
          {/* Field 22 */}
          <div style={{ padding: '3px 5px', borderBottom: '1px solid #ccc' }}>
            <div style={{ fontSize: 7.5, fontWeight: 700, marginBottom: 3 }}>22. REQUIRED RESOLUTIONS ARE ATTACHED</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {['No','Yes','N/A'].map(opt => <FormCheckbox key={opt} checked={(s215.resolutions||'Yes')===opt} onChange={() => updateS215('resolutions', opt)} label={opt} />)}
            </div>
          </div>
          {/* Field 23 */}
          <div style={{ padding: '3px 5px' }}>
            <div style={{ fontSize: 7.5, fontWeight: 700, marginBottom: 2 }}>23. IS THIS A SMALL BUSINESS AND/OR A DISABLED VETERAN BUSINESS CERTIFIED BY DGS?</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
              <FormCheckbox checked={!contract.sbDvbeCertNum} onChange={()=>{}} label="No" />
              <FormCheckbox checked={!!contract.sbDvbeCertNum} onChange={()=>{}} label="Yes" />
            </div>
            <div style={{ fontSize: 7, color: '#555' }}>SB/DVBE Certification Number:</div>
            <div style={{ borderBottom: '1px solid #999', fontSize: 10, padding: '1px 2px', minHeight: 16 }}>
              {contract.sbDvbeCertNum || ''}
            </div>
          </div>
        </div>
      </div>

      {/* Field 24 */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '4px 6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 7.5, flex: 1 }}><strong>24.</strong> ARE DISABLED VETERANS BUSINESS ENTERPRISE GOALS REQUIRED? (If an amendment, explain changes if any)</div>
          <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
            <FormCheckbox checked={!s215.dvbeGoalsRequired || s215.dvbeGoalsRequired==='no'} onChange={() => updateS215('dvbeGoalsRequired','no')} label="No (Explain below)" />
            <FormCheckbox checked={s215.dvbeGoalsRequired==='yes'} onChange={() => updateS215('dvbeGoalsRequired','yes')} label="Yes" />
            {s215.dvbeGoalsRequired==='yes' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <FormInput value={s215.dvbePct||''} onChange={v => updateS215('dvbePct',v)} style={{ width: 40, borderBottom: '1px solid #999' }} />
                <span style={{ fontSize: 9 }}>% of Agreement</span>
              </div>
            )}
          </div>
        </div>
        {(!s215.dvbeGoalsRequired || s215.dvbeGoalsRequired === 'no') && (
          <div style={{ borderTop: '1px solid #ccc', marginTop: 4, paddingTop: 3 }}>
            <div style={{ fontSize: 7, color: '#555', marginBottom: 2 }}>EXPLANATION (why DVBE goals are not required):</div>
            <FormTextarea
              value={s215.dvbeGoalsExplanation || ''}
              onChange={v => updateS215('dvbeGoalsExplanation', v)}
              rows={2}
              placeholder="Explain why DVBE goals are not required for this agreement..."
            />
          </div>
        )}
      </FormBox>

      {/* Field 25 */}
      <FormBox style={{ borderTop: '1px solid #333', padding: '4px 6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 7.5, flex: 1 }}><strong>25.</strong> IS THIS AGREEMENT (WITH AMENDMENTS) FOR A PERIOD OF TIME LONGER THAN THREE YEARS?</div>
          <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
            {(() => {
              const longer = s215.startDate && s215.endDate
                ? (new Date(s215.endDate) - new Date(s215.startDate)) / (1000*60*60*24*365.25) > 3
                : false;
              return (
                <>
                  <FormCheckbox checked={!longer} onChange={()=>{}} label="No" />
                  <FormCheckbox checked={longer} onChange={()=>{}} label="Yes (If Yes, provide justification below)" />
                </>
              );
            })()}
          </div>
        </div>
        {(() => {
          const longer = s215.startDate && s215.endDate
            ? (new Date(s215.endDate) - new Date(s215.startDate)) / (1000*60*60*24*365.25) > 3
            : false;
          return longer ? (
            <div style={{ borderTop: '1px solid #ccc', marginTop: 4, paddingTop: 3 }}>
              <div style={{ fontSize: 7, color: '#555', marginBottom: 2 }}>JUSTIFICATION (why the agreement term exceeds three years):</div>
              <FormTextarea
                value={s215.longerTermJustification || ''}
                onChange={v => updateS215('longerTermJustification', v)}
                rows={2}
                placeholder="Explain why the agreement term is longer than three years..."
              />
            </div>
          ) : null;
        })()}
      </FormBox>

      {/* Final certification */}
      <FormBox style={{ border: '1px solid #333', padding: '4px 6px' }}>
        <div style={{ fontSize: 7.5, fontStyle: 'italic', marginBottom: 6 }}>I certify that all copies of the referenced Agreement will conform to the original agreement sent to the Department of General Services.</div>
        <Row style={{ borderTop: '1px solid #333' }}>
          <Col flex={2} style={{ borderLeft: '1px solid #333', minHeight: 22 }}><FieldLabel>SIGNATURE</FieldLabel></Col>
          <Col flex={3}>
            <FieldLabel>NAME/TITLE <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Print or Type)</span></FieldLabel>
            <FormInput value={(s215.signatoryName || profile.signatoryName || '') + (s215.signatoryTitle || profile.signatoryTitle ? ', ' + (s215.signatoryTitle || profile.signatoryTitle) : '')} onChange={v => updateS215('signatoryNameOverride', v)} />
          </Col>
          <Col style={{ borderRight: '1px solid #333' }}><FieldLabel>DATE SIGNED</FieldLabel></Col>
        </Row>
      </FormBox>
    </div>
  );
}

function Page3({ s215, contract, profile, updateS215 }) {
  const scoId = s215.scoId || ((profile.scoAgencyCode || '3355') + (contract.contractNum ? '-' + contract.contractNum : ''));
  return (
    <div style={{ fontSize: 9.5, fontFamily: 'Arial, sans-serif' }}>
      <PageHeader
        agreementNum={s215.agreementNum || contract.contractNum}
        amendmentNum={s215.amendmentNum}
        scoId={scoId}
        pageNum={3}
      />

      <div style={{ fontSize: 11, fontWeight: 900, marginBottom: 4 }}>
        JUSTIFICATION - CALIFORNIA CODE OF REGULATIONS, TITLE 2, SECTION 547.60
      </div>
      <div style={{ fontSize: 8.5, marginBottom: 8, lineHeight: 1.5 }}>
        In the space provided below, the undersigned authorized state representative documents, with specificity and detailed factual information, the reasons why the contract satisfies one or more of the conditions set forth in Government Code section 19130(b). Please specify the applicable subsection. Attach extra pages if necessary.
      </div>

      <FormBox style={{ minHeight: 280, padding: 4 }}>
        <FormTextarea
          value={s215.justification547}
          onChange={v => updateS215('justification547', v)}
          rows={16}
          placeholder="Enter your GC 19130(b) justification here. Specify the applicable subsection and provide detailed factual information..."
        />
      </FormBox>

      <div style={{ fontSize: 8, fontStyle: 'italic', margin: '6px 0 4px', lineHeight: 1.5 }}>
        The undersigned represents that, based upon his or her personal knowledge, information or belief the above justification correctly reflects the reasons why the contract satisfies Government Code section 19130(b).
      </div>

      <Row style={{ borderBottom: '1px solid #333' }}>
        <Col flex={2} style={{ borderLeft: '1px solid #333', borderTop: '1px solid #333', minHeight: 28 }}>
          <FieldLabel>SIGNATURE</FieldLabel>
        </Col>
        <Col flex={3} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>NAME/TITLE <span style={{ fontWeight: 400, fontStyle: 'italic' }}>(Print or Type)</span></FieldLabel>
          <FormInput value={(s215.signatoryName || profile.signatoryName || '') + (s215.signatoryTitle || profile.signatoryTitle ? ', ' + (s215.signatoryTitle || profile.signatoryTitle) : '')} onChange={v => updateS215('signatoryNameOverride', v)} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>DATE SIGNED</FieldLabel>
        </Col>
      </Row>
      <Row style={{ borderBottom: '1px solid #333' }}>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', minHeight: 24 }}>
          <FieldLabel>PHONE NUMBER</FieldLabel>
          <FormInput value={s215.signatoryPhoneOverride !== undefined ? s215.signatoryPhoneOverride : (s215.signatoryPhone || profile.signatoryPhone || '')} onChange={v => updateS215('signatoryPhoneOverride', v)} placeholder={profile.signatoryPhone || 'Phone'} />
        </Col>
        <Col flex={3} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>STREET ADDRESS</FieldLabel>
          <FormInput value={s215.signatoryAddressOverride !== undefined ? s215.signatoryAddressOverride : (profile.signatoryAddress || '')} onChange={v => updateS215('signatoryAddressOverride', v)} placeholder={profile.signatoryAddress || 'Street address'} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>EMAIL</FieldLabel>
          <FormInput value={s215.signatoryEmailOverride !== undefined ? s215.signatoryEmailOverride : (s215.signatoryEmail || profile.signatoryEmail || '')} onChange={v => updateS215('signatoryEmailOverride', v)} style={{ fontSize: 9 }} placeholder={profile.signatoryEmail || 'Email'} />
        </Col>
      </Row>
      <Row style={{ borderBottom: '1px solid #333' }}>
        <Col flex={3} style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', minHeight: 22 }}>
          <FieldLabel>CITY</FieldLabel>
          <FormInput value={s215.signatoryCityOverride !== undefined ? s215.signatoryCityOverride : (profile.signatoryCity || '')} onChange={v => updateS215('signatoryCityOverride', v)} placeholder={profile.signatoryCity || 'City'} />
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333' }}>
          <FieldLabel>STATE</FieldLabel>
          <div style={{ padding: '2px 3px', fontSize: 10 }}>CA</div>
        </Col>
        <Col style={{ borderTop: '1px solid #333', borderLeft: '1px solid #333', borderRight: '1px solid #333' }}>
          <FieldLabel>ZIP</FieldLabel>
          <FormInput value={s215.signatoryZipOverride !== undefined ? s215.signatoryZipOverride : (profile.signatoryZip || '')} onChange={v => updateS215('signatoryZipOverride', v)} placeholder={profile.signatoryZip || 'ZIP'} />
        </Col>
      </Row>
    </div>
  );
}

// ── Source sync maps — fields that pre-populate from profile or contract ───

const PROFILE_SYNC_MAP = {
  agencyOverride:            { profileKey: 'agencyName',             label: 'Agency Name',               where: 'User Profile → Agency Name' },
  divisionOverride:          { profileKey: 'agencyDivision',         label: 'Division / Bureau',          where: 'User Profile → Agency Division' },
  agencyBillingCode:         { profileKey: 'agencyBillingCode',      label: 'Agency Billing Code',        where: 'User Profile → Agency Billing Code' },
  contactName:               { profileKey: 'contactName',            label: 'Contract Analyst Name',      where: 'User Profile → Contact Name' },
  contactEmail:              { profileKey: 'contactEmail',           label: 'Analyst Email',              where: 'User Profile → Contact Email' },
  contactPhone:              { profileKey: 'contactPhone',           label: 'Analyst Phone',              where: 'User Profile → Contact Phone' },
  accountingOfficerOverride: { profileKey: 'accountingOfficerName',  label: 'Accounting Officer Name',    where: 'User Profile → Accounting Officer Name' },
  signatoryNameOverride:     { profileKey: 'signatoryName',          label: 'Signatory Name',             where: 'User Profile → Signatory Name' },
  signatoryPhoneOverride:    { profileKey: 'signatoryPhone',         label: 'Signatory Phone',            where: 'User Profile → Signatory Phone' },
  signatoryEmailOverride:    { profileKey: 'signatoryEmail',         label: 'Signatory Email',            where: 'User Profile → Signatory Email' },
  signatoryAddressOverride:  { profileKey: 'signatoryAddress',       label: 'Signatory Street Address',   where: 'User Profile → Signatory Address' },
  signatoryCityOverride:     { profileKey: 'signatoryCity',          label: 'Signatory City',             where: 'User Profile → Signatory City' },
  signatoryZipOverride:      { profileKey: 'signatoryZip',           label: 'Signatory ZIP',              where: 'User Profile → Signatory ZIP' },
};

const CONTRACT_SYNC_MAP = {
  descriptionOverride: { contractKey: 'title',       label: 'Brief Description of Services', where: 'Contract → Title' },
  maxAmount:           { contractKey: 'budget',      label: 'Agreement Total / Budget',      where: 'Contract → Budget' },
  agreementNum:        { contractKey: 'contractNum', label: 'Agreement Number',              where: 'Contract → Contract No.' },
};

// ── Main Tab ───────────────────────────────────────────────────────────────

const PAGE_W = 780;  // px width for PDF capture — scales to letter size

export default function STD215Tab({ contract, updateC, updateS215: updateS215Raw, s215, profile, paal, onUpdateProfile }) {
  const [page, setPage] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [pendingSync, setPendingSync] = useState([]);
  const pdfContainerRef = useRef(null);

  // Wrap updateS215 — saves immediately, then queues a sync prompt if the field has a source
  function updateS215(key, value) {
    updateS215Raw(key, value);

    // Check profile-sourced fields
    if (PROFILE_SYNC_MAP[key]) {
      const { profileKey, label, where } = PROFILE_SYNC_MAP[key];
      const profileVal = profile[profileKey];
      // Only prompt if the profile has a value and the new value differs from it
      if (profileVal && value && value !== profileVal) {
        setPendingSync(prev => {
          const filtered = prev.filter(p => p.key !== key);
          return [...filtered, { key, value, label, where, source: 'profile', profileKey }];
        });
      }
      return;
    }

    // Check contract-sourced fields
    if (CONTRACT_SYNC_MAP[key]) {
      const { contractKey, label, where } = CONTRACT_SYNC_MAP[key];
      const contractVal = String(contract[contractKey] || '');
      if (contractVal && value && value !== contractVal) {
        setPendingSync(prev => {
          const filtered = prev.filter(p => p.key !== key);
          return [...filtered, { key, value, label, where, source: 'contract', contractKey }];
        });
      }
    }
  }

  function handleApplyToSource() {
    const profileUpdates = {};
    const contractUpdates = {};
    pendingSync.forEach(item => {
      if (item.source === 'profile') profileUpdates[item.profileKey] = item.value;
      if (item.source === 'contract') contractUpdates[item.contractKey] = item.value;
    });
    if (Object.keys(profileUpdates).length > 0 && onUpdateProfile) {
      onUpdateProfile(profileUpdates);
    }
    if (Object.keys(contractUpdates).length > 0) {
      updateC(contractUpdates);
    }
    setPendingSync([]);
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement('script');
      s.src = src; s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  async function handleDownloadPDF() {
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
    await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
    const h2c = window.html2canvas;
    const jsPDFLib = window.jspdf?.jsPDF || window.jsPDF;
    if (!h2c || !jsPDFLib) {
      alert('PDF library not loaded. Please refresh and try again.');
      return;
    }
    setGenerating(true);
    let clone = null;
    try {
      const container = pdfContainerRef.current;
      container.style.display = 'block';

      // Clone the container so we can swap inputs → divs without touching live DOM
      clone = container.cloneNode(true);
      clone.style.position = 'fixed';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.zIndex = '-1';
      clone.style.display = 'block';
      document.body.appendChild(clone);

      // Step 1: Replace every input/textarea/select with a plain div
      // Use explicit padding (not computed) to avoid browser-native input quirks
      clone.querySelectorAll('input, textarea, select').forEach(el => {
        const isTextarea = el.tagName === 'TEXTAREA';
        const computed = window.getComputedStyle(el);
        const div = document.createElement('div');
        div.textContent = el.value || '';
        div.style.fontSize = computed.fontSize;
        div.style.fontFamily = computed.fontFamily;
        div.style.fontWeight = computed.fontWeight;
        div.style.color = computed.color || '#111';
        // Use generous explicit padding — don't rely on computed which differs per browser
        div.style.padding = isTextarea ? '2px 3px 4px 3px' : '3px 3px 5px 3px';
        div.style.lineHeight = '1.45';
        div.style.width = '100%';
        div.style.boxSizing = 'border-box';
        div.style.background = 'transparent';
        div.style.border = 'none';
        div.style.whiteSpace = isTextarea ? 'pre-wrap' : 'nowrap';
        div.style.wordBreak = 'break-word';
        div.style.overflow = 'visible';
        div.style.height = 'auto';
        div.style.minHeight = '0';
        div.style.display = 'block';
        // Also unlock the parent so it doesn't clip the div
        if (el.parentNode && el.parentNode.style) {
          el.parentNode.style.overflow = 'visible';
          el.parentNode.style.height = 'auto';
        }
        el.parentNode.replaceChild(div, el);
      });

      // Step 2: Walk every element in the clone and remove any overflow clipping
      // Skip the page containers themselves so layout doesn't collapse
      clone.querySelectorAll('*').forEach(el => {
        if (el.classList && el.classList.contains('pdf-capture-page')) return;
        const cs = window.getComputedStyle(el);
        if (cs.overflow === 'hidden' || cs.overflowY === 'hidden') {
          el.style.overflow = 'visible';
        }
        // If element has a fixed pixel height that could clip text, switch to minHeight
        // Skip tiny elements (≤15px) like checkbox squares — converting those to auto collapses them into dashes
        if (cs.height && cs.height !== 'auto' && parseInt(cs.height) > 15 && parseInt(cs.height) < 200) {
          el.style.height = 'auto';
          el.style.minHeight = cs.height;
        }
      });

      const pages = clone.querySelectorAll('.pdf-capture-page');
      const pdf = new jsPDFLib({ unit: 'pt', format: 'letter', orientation: 'portrait' });
      for (let i = 0; i < pages.length; i++) {
        const canvas = await h2c(pages[i], {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          width: PAGE_W,
          windowWidth: PAGE_W,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.97);
        // Page capture is now exactly letter-proportioned — use standard 612×792pt
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, 612, 792);
      }

      container.style.display = 'none';
      pdf.save((s215.agreementNum || contract.contractNum || 'STD215') + '_Agreement_Summary.pdf');
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      if (clone) document.body.removeChild(clone);
    }
    setGenerating(false);
  }

  const pageProps1 = { s215, contract, profile, updateS215 };
  const pageProps2 = { s215, contract, profile, paal, updateS215 };
  const pageProps3 = { s215, contract, profile, updateS215 };

  const pageStyle = {
    width: PAGE_W,
    minHeight: Math.round(PAGE_W * 11 / 8.5), // 1009px — exact letter proportion, prevents Windows PDF viewer boundary line
    background: 'white',
    padding: '24px 28px',
    boxSizing: 'border-box',
    pageBreakAfter: 'always',
    overflow: 'visible',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy-900)', margin: 0 }}>STD 215 — Agreement Summary</h2>
          <p style={{ fontSize: 12, color: 'var(--slate-500)', margin: '2px 0 0' }}>Edit fields directly on the form. All changes save automatically.</p>
        </div>
        <button
          className="btn btn-gold"
          onClick={handleDownloadPDF}
          disabled={generating}
          style={{ minWidth: 130 }}
        >
          {generating ? '⏳ Generating...' : '⬇ Download PDF'}
        </button>
      </div>

      {/* Page switcher */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
        {[1,2,3].map(p => (
          <button
            key={p}
            onClick={() => setPage(p)}
            className="btn"
            style={{
              background: page === p ? 'var(--navy-700)' : 'var(--white)',
              color: page === p ? 'var(--white)' : 'var(--slate-600)',
              borderColor: page === p ? 'var(--navy-700)' : 'var(--slate-200)',
              fontWeight: page === p ? 700 : 400,
            }}
          >
            Page {p} {p===1?'— Contractor & Expenditures':p===2?'— Agreement & Compliance':'— GC 19130(b) Justification'}
          </button>
        ))}
      </div>

      {/* Source sync prompt banner */}
      {pendingSync.length > 0 && (
        <div style={{
          background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
            💡 You changed field(s) connected to source data — update the source too?
          </div>
          <div style={{ marginBottom: 8 }}>
            {pendingSync.map((item, i) => (
              <div key={i} style={{ fontSize: 11.5, color: '#78350f', marginBottom: 3, display: 'flex', gap: 6 }}>
                <span style={{ fontWeight: 700 }}>{item.label}</span>
                <span style={{ color: '#b45309' }}>→ "{item.value}"</span>
                <span style={{ color: '#a16207' }}>will update <strong>{item.where}</strong></span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleApplyToSource}
              style={{
                background: '#f59e0b', color: 'white', border: 'none', borderRadius: 5,
                padding: '5px 12px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              }}
            >
              ✓ Update Source
            </button>
            <button
              onClick={() => setPendingSync([])}
              style={{
                background: 'white', color: '#92400e', border: '1px solid #f59e0b',
                borderRadius: 5, padding: '5px 12px', fontSize: 11.5, cursor: 'pointer',
              }}
            >
              This contract only
            </button>
          </div>
        </div>
      )}

      {/* Visible form */}
      <div style={{
        background: 'white',
        border: '1px solid var(--slate-300)',
        borderRadius: 'var(--r-lg)',
        padding: '20px 24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        maxWidth: 860,
      }}>
        {page === 1 && <Page1 {...pageProps1} />}
        {page === 2 && <Page2 {...pageProps2} />}
        {page === 3 && <Page3 {...pageProps3} />}
      </div>

      {/* Hidden PDF capture container — all 3 pages rendered simultaneously */}
      <div
        ref={pdfContainerRef}
        style={{ display: 'none', position: 'fixed', top: -9999, left: -9999, zIndex: -1 }}
      >
        <div className="pdf-capture-page" style={pageStyle}><Page1 {...pageProps1} /></div>
        <div className="pdf-capture-page" style={pageStyle}><Page2 {...pageProps2} /></div>
        <div className="pdf-capture-page" style={pageStyle}><Page3 {...pageProps3} /></div>
      </div>


      {/* ── Signed / Completed Document Upload ─────────────────── */}
      <div style={{ marginTop: 20, padding: '16px 20px', borderRadius: 'var(--r-lg)', border: '1.5px solid ' + (contract.s215?.signedDoc ? 'var(--green-300)' : 'var(--slate-200)'), background: contract.s215?.signedDoc ? 'var(--green-50)' : 'var(--slate-50)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: contract.s215?.signedDoc ? 10 : 0 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: contract.s215?.signedDoc ? 'var(--green-700)' : 'var(--navy-700)' }}>
              {contract.s215?.signedDoc ? '✅ Signed STD 215 on file' : '📋 Upload Signed / Completed STD 215'}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--slate-400)', marginTop: 2 }}>
              Upload the fully executed STD 215 after signatures — available for export in the Assembly tab
            </div>
          </div>
          {contract.s215?.signedDoc && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={async () => {
                const doc = contract.s215.signedDoc;
                if (doc.storagePath || doc.base64) {
                  let blob;
                  if (doc.storagePath) {
                    const supabase = (await import('../../utils/supabaseClient.js')).default;
                    const { data } = await supabase.storage.from('exhibit-files').download(doc.storagePath);
                    blob = data;
                  } else {
                    const bytes = atob(doc.base64); const arr = new Uint8Array(bytes.length);
                    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                    blob = new Blob([arr], { type: 'application/pdf' });
                  }
                  if (blob) { const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = doc.fileName || 'STD215_Signed.pdf'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url); }
                }
              }}>↓ Download</button>
              <button className="btn btn-ghost btn-sm" onClick={() => document.getElementById('std215-signed-upload').click()}>↑ Replace</button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red-400)' }} onClick={() => updateS215('signedDoc', null)}>✕ Remove</button>
            </div>
          )}
        </div>
        {contract.s215?.signedDoc && (
          <div style={{ fontSize: 12, color: 'var(--green-600)' }}>
            {contract.s215.signedDoc.fileName} · Uploaded {contract.s215.signedDoc.uploadedAt ? new Date(contract.s215.signedDoc.uploadedAt).toLocaleDateString() : ''}
          </div>
        )}
        {!contract.s215?.signedDoc && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: '1.5px dashed var(--slate-300)', borderRadius: 'var(--r-md)', cursor: 'pointer', background: '#fff', marginTop: 10 }}>
            <span style={{ fontSize: 22 }}>📋</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate-700)' }}>Click to upload signed STD 215</div>
              <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 2 }}>PDF only — fully executed with all signatures</div>
            </div>
            <input id="std215-signed-upload" type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={async e => {
                const file = e.target.files[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = async () => {
                  const base64 = reader.result.split(',')[1];
                  let storagePath = null;
                  try {
                    const supabase = (await import('../../utils/supabaseClient.js')).default;
                    const { data: { user } } = await supabase.auth.getUser();
                    if (user && contract?.id) {
                      const path = user.id + '/' + contract.id + '/std215_signed_' + file.name.replace(/\s+/g, '_');
                      const bytes = atob(base64); const arr = new Uint8Array(bytes.length);
                      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                      const { error } = await supabase.storage.from('exhibit-files').upload(path, new Blob([arr], { type: 'application/pdf' }), { upsert: true });
                      if (!error) storagePath = path;
                    }
                  } catch (e) { console.warn('STD 215 signed upload error:', e.message); }
                  updateS215('signedDoc', { fileName: file.name, size: file.size, storagePath, base64: storagePath ? null : base64, uploadedAt: new Date().toISOString() });
                };
                reader.readAsDataURL(file);
              }} />
          </label>
        )}
        <input id="std215-signed-upload" type="file" accept=".pdf" style={{ display: 'none' }}
          onChange={async e => {
            const file = e.target.files[0]; if (!file) return;
            const reader = new FileReader();
            reader.onload = async () => {
              const base64 = reader.result.split(',')[1];
              let storagePath = null;
              try {
                const supabase = (await import('../../utils/supabaseClient.js')).default;
                const { data: { user } } = await supabase.auth.getUser();
                if (user && contract?.id) {
                  const path = user.id + '/' + contract.id + '/std215_signed_' + file.name.replace(/\s+/g, '_');
                  const bytes = atob(base64); const arr = new Uint8Array(bytes.length);
                  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                  const { error } = await supabase.storage.from('exhibit-files').upload(path, new Blob([arr], { type: 'application/pdf' }), { upsert: true });
                  if (!error) storagePath = path;
                }
              } catch (e) { console.warn('STD 215 signed upload error:', e.message); }
              updateS215('signedDoc', { fileName: file.name, size: file.size, storagePath, base64: storagePath ? null : base64, uploadedAt: new Date().toISOString() });
            };
            reader.readAsDataURL(file);
          }} />
      </div>

      <p style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 10 }}>
        Fields shown in gray auto-fill from your User Profile. Edit them in <strong>Configuration → User Profile</strong>.
      </p>
    </div>
  );
}
